// Constants
import { WatchlistProviders } from 'app/modules/detectionModels/components/scenarioWidgets/watchlist/enums';
import { UNLOADED_FLAGS_KEY } from 'app/shared/featureFlags/constants';

export enum FeatureFlagsActionTypes {
  SET_FEATURE_FLAGS = '@@featureFlags/SET_FEATURE_FLAGS',
}

// This enum should only contain active flags
// (makes archiving easier as tsc will complain when you pluck out a no-longer-needed flag)
export enum FeatureFlag {
  // Investigations
  AI_ADMIN_DASHBOARD = 'ai-admin-dashboard',
  EDIT_CUSTOM_DATA = 'edit-custom-data',
  ALERT_COMPONENT_BUTTONS = 'alert-component-buttons',
  SHOW_PAYER_PAYEE_TABS = 'show-payer-payee-tabs',
  REMOVE_DISPLAY_TAGS = 'remove-display-tags',
  BULK_ACTION_LIMIT = 'bulk-action-limit',
  ALERT_FROM_ALERT = 'alert-from-alert',
  IMMUTABLE_NOTES = 'immutable-notes',
  WORKAROUND_HIDE_CURRENCY_CONFIG = 'workaround-hide-currency-code',
  CLASSIFIED_QUEUES = 'classified-queues',
  DISPOSITION_ACTIONS = 'disposition-actions',
  WATCHLIST_ALERTS = 'watchlist-alerts',
  ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING = 'alert-table-amount-column-skeleton-loading',
  QUALITY_CYCLE_LIMIT = 'quality-cycle-limit',
  WATCHLIST_PROVIDER = 'watchlist-provider',
  BULK_RESOLVE_FROM_QUEUED_VIEWS = 'bulk-resolve-from-queued-views',
  MULTI_CASE_FILING = 'MULTI_CASE_FILING',
  ADD_CASES_TO_SAR = 'ADD_CASES_TO_SAR',
  FINCEN_314A = '314a',
  FINCEN_314A_MATCHING_LOGIC_VERSION = '314-a-matching-logic-version',
  FINTRAC_STR = 'fintrac-str',
  FINTRAC_STR_UPLOAD = 'fintrac-str-upload',
  FINTRAC_STR_AUTOFILL_WORKFLOW = 'fintrac-str-autofill-workflow',
  FINTRAC_STR_AUTOFILL_DISPOSITION_COMMENT = 'fintrac-str-autofill-disposition-comment',
  FINTRAC_FORM_GENERATOR_REFACTOR = 'fintrac-form-generator-refactor',
  FINCEN_DOEP_FILING = 'FINCEN_DOEP_FILING',
  ROUND_ROBIN_ALERT_ASSIGNMENT = 'round-robin-alert-assignment',
  // SC #116590 — gates entity/instrument grouping for both auto-assignment
  // (schedule types) and the "Get More" pull. Value matches the LD flag key
  // the backend (dumbledore/patronus) reads verbatim, so one flag drives all.
  ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT = 'ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT',
  SHOW_TRANSACTION_ACTIVITY = 'show-transaction-activity',
  TEAM_BASED_WORKFLOW_PERMISSIONS = 'team-based-workflow-permissions',
  WORKFLOW_MANAGEMENT_REDESIGN = 'workflow-management-redesign',
  FINTRAC_ERROR_CARDS = 'fintrac-error-cards',
  REPORTING_DASHBOARD_V2 = 'reporting-dashboard-v-2',

  // AI
  AI_AGENT = 'ai-agent',
  AI_AGENT_DATA_EXPLORATION = 'ai-agent-data-exploration',
  ASK_YOUR_ALERT = 'ask-your-alert',
  BUILD_YOUR_OWN_AGENT = 'build-your-own-agent',
  ENABLE_FILING_AI_AGENTS = 'enable-filing-ai-agents',
  AI_DECISIONING_V0 = 'ai-decisioning-v-0',
  ONLINE_RESEARCH_BYOT = 'byot-online-research',
  G_GROUNDING_ONLINE_RESEARCH = 'g-grounding-online-research',
  GREENDOT_PIVOT_TABLES = 'greendot-pivot-tables',

  // Delta Lake Transition Flags
  // Enable loading CGDO on demand when an alert/case/sar is looked at
  PG_CACHE_AD_HOC_DATA_LOADER = 'PG_CACHE_AD_HOC_DATA_LOADER',

  // Alert Pages
  CM_DELTA_LAKE_ALERT_LIST = 'CM_DELTA_LAKE_ALERT_LIST',
  CM_DELTA_LAKE_ALERT_DETAIL = 'CM_DELTA_LAKE_ALERT_DETAIL',
  CM_DELTA_LAKE_ALERT_TXN_ANALYSIS = 'CM_DELTA_LAKE_ALERT_TXN_ANALYSIS',
  CM_DELTA_LAKE_ALERT_TXN_LIST = 'CM_DELTA_LAKE_ALERT_TXN_LIST',
  CM_DELTA_LAKE_ALERT_ACTION_LIST = 'CM_DELTA_LAKE_ALERT_ACTION_LIST',
  CM_DELTA_LAKE_ALERT_HISTOGRAM = 'CM_DELTA_LAKE_ALERT_HISTOGRAM',

  // Case Pages
  CM_DELTA_LAKE_CASE_LIST = 'CM_DELTA_LAKE_CASE_LIST',
  CM_DELTA_LAKE_CASE_CGDO_SEARCH = 'CM_DELTA_LAKE_CASE_CGDO_SEARCH',
  CM_DELTA_LAKE_CASE_DETAIL = 'CM_DELTA_LAKE_CASE_DETAIL',
  CM_DELTA_LAKE_CASE_TXN_ANALYSIS = 'CM_DELTA_LAKE_CASE_TXN_ANALYSIS',
  CM_DELTA_LAKE_CASE_ENTITIES = 'CM_DELTA_LAKE_CASE_ENTITIES',
  CM_DELTA_LAKE_CASE_ACTION_EVENTS = 'CM_DELTA_LAKE_CASE_ACTION_EVENTS',

  // Filing Pages
  CM_DELTA_LAKE_FILING_SAR_LIST = 'CM_DELTA_LAKE_FILING_SAR_LIST',
  CM_DELTA_LAKE_FILING_SAR_CREATE = 'CM_DELTA_LAKE_FILING_SAR_CREATE',
  CM_DELTA_LAKE_FILING_CTR_LIST = 'CM_DELTA_LAKE_FILING_CTR_LIST',
  CM_DELTA_LAKE_FILING_CTR_CREATE = 'CM_DELTA_LAKE_FILING_CTR_CREATE',
  CM_DELTA_LAKE_FILING_GOAML_LIST = 'CM_DELTA_LAKE_FILING_GOAML_LIST',
  CM_DELTA_LAKE_FILING_GOAML_CREATE = 'CM_DELTA_LAKE_FILING_GOAML_CREATE',
  CM_DELTA_LAKE_FILING_SAR_DETAIL = 'CM_DELTA_LAKE_FILING_SAR_DETAIL',
  CM_DELTA_LAKE_FILING_CTR_DETAIL = 'CM_DELTA_LAKE_FILING_CTR_DETAIL',
  CM_DELTA_LAKE_GOAML_DETAIL = 'CM_DELTA_LAKE_GOAML_DETAIL',
  CM_DELTA_LAKE_FILING_FINTRAC_CREATE = 'CM_DELTA_LAKE_FILING_FINTRAC_CREATE',

  // Dashboard Pages
  CM_DELTA_LAKE_FRAUD_DASHBOARD = 'CM_DELTA_LAKE_FRAUD_DASHBOARD',

  // Data Explorer Pages
  CM_DELTA_LAKE_TXN_EVENT_LIST = 'CM_DELTA_LAKE_TXN_EVENT_LIST',
  CM_DELTA_LAKE_TXN_EVENT_DETAIL = 'CM_DELTA_LAKE_TXN_EVENT_DETAIL',
  CM_DELTA_LAKE_ACTION_EVENT_LIST = 'CM_DELTA_LAKE_ACTION_EVENT_LIST',
  CM_DELTA_LAKE_ACTION_EVENT_DETAIL = 'CM_DELTA_LAKE_ACTION_EVENT_DETAIL',
  CM_DELTA_LAKE_INSTRUMENT_LIST = 'CM_DELTA_LAKE_INSTRUMENT_LIST',
  CM_DELTA_LAKE_INSTRUMENT_DETAIL = 'CM_DELTA_LAKE_INSTRUMENT_DETAIL',
  CM_DELTA_LAKE_INSTRUMENT_RELATIONS = 'CM_DELTA_LAKE_INSTRUMENT_RELATIONS',
  CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS = 'CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS',
  CM_DELTA_LAKE_ENTITY_LIST = 'CM_DELTA_LAKE_ENTITY_LIST',
  CM_DELTA_LAKE_ENTITY_DETAIL = 'CM_DELTA_LAKE_ENTITY_DETAIL',
  CM_DELTA_LAKE_ENTITY_RISK_SCORE = 'CM_DELTA_LAKE_ENTITY_RISK_SCORE',
  CM_DELTA_LAKE_NETWORK_ANALYSIS = 'CM_DELTA_LAKE_NETWORK_ANALYSIS',
  CM_DELTA_LAKE_ENTITY_RELATIONS = 'CM_DELTA_LAKE_ENTITY_RELATIONS',
  CM_DELTA_LAKE_ENTITY_TXN_EVENTS = 'CM_DELTA_LAKE_ENTITY_TXN_EVENTS',
  CM_DELTA_LAKE_ENTITY_ACTION_EVENTS = 'CM_DELTA_LAKE_ENTITY_ACTION_EVENTS',
  CM_DELTA_LAKE_ENTITY_GEOLOCATION = 'CM_DELTA_LAKE_ENTITY_GEOLOCATION',
  CM_DELTA_LAKE_HASHED_ID_ROUTES = 'CM_DELTA_LAKE_HASHED_ID_ROUTES',

  // DIM
  DARK_WEB_ALERTS = 'dark-web-alerts',
  CONSORTIUM_DMB = 'consortium-dmb',
  CONSORTIUM_ALERTS_SUMMARY = 'consortium-alerts-summary',
  CONSORTIUM_ALERTS_V2 = 'consortium-alerts-v2',

  // DM
  SIXTRAN_SNOWFLAKE_EXPECTED_LAG = 'sixtran-snowflake-expected-lag',
  FRAUD_TRANSACTION_RULES = 'fraud-transaction-rules',
  RULE_DELAY = 'rule-delay',
  ACH_RISK_SCORE_SCENARIO_ENABLED = 'ach-risk-score-scenario-enabled',
  ACH_RISK_SCORE_FIELD = 'ach-risk-score-field',
  HISTORIC_RISK_SCORES = 'historic-risk-scores',
  RULE_EVENT_SCHEMA_V2_1 = 'rule-event-schema-v-2-1',
  FOCSEC_FIELDS_ENABLED = 'focsec-fields-enabled',
  DMB_CUSTOM_TXN_DATETIME = 'dmb-custom-txn-datetime',
  VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS = 'rtr-entity-instrument-aggregation-filters',
  VELOCITY_RTR_MIXED_EVENT_TYPES = 'velocity-rtr-mixed-event-types',
  VELOCITY_RTR_DEVICE_INTELLIGENCE_FIELDS = 'rtr-device-intelligence-fields',
  RTR_ENTITY_ALLOWLISTING = 'rtr-entity-allowlisting',
  RTR_TRM_ENABLED = 'rtr-trm-enabled',
  RULE_RECOMMENDATIONS = 'rule-recommendations',
  RULE_RECS_CREATE_RULE = 'rule-recs-create-rule',
  DMB_FORM_REFACTOR = 'dmb-form-refactor',
  WATCHLIST_COMBINED_JOURNEYS = 'watchlist-combined-journeys',
  AI_TEXT_TO_RULE = 'ai-text-to-rule',
  DMB_AI_FUZZY_SCORE = 'dmb-ai-fuzzy-score',
  GLOBAL_VARIABLES = 'global-variables',
  THRESHOLD_EXPLORER = 'threshold-explorer',
  THRESHOLD_EXPLORER_DASHBOARDS = 'threshold-explorer-dashboards',
  SR_TRM_DMB_ENABLED = 'sr-trm-dmb-enabled',
  DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED = 'alert-hit-cgdo-historical-lookup-enabled',
  DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS = 'alert-hit-cgdo-historical-lookup-for-all-agents',
  RULES_AVOID_ALERT_GENERATION = 'rules-avoid-alert-generation',

  // DE
  SEARCH_V2 = 'SEARCH_V2',
  ENTITY_PHONE_NUMBER_SEARCH_V2 = 'entity-phone-number-search-v2',
  CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID = 'CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID',
  FFIP_ENRICHMENT_MATCHING = 'ffip-enrichment-matching',

  // DevOps
  RULE_LIFECYCLE_BANNER = 'rule-lifecycle-banner',

  // Instrument Transaction Analysis
  INSTRUMENT_TXN_ANALYSIS = 'INSTRUMENT_TXN_ANALYSIS',

  // Activity Analysis Update
  ACTION_ANALYSIS_UPDATE = 'action-analysis-update',

  // Misc
  SESSION_REPLAY = 'sentry-session-replay',
  FEEDBACK_SURVEY_ID = 'feedback-survey-id',
  ALLOW_AI_AGENT_RE_RUN = 'ALLOW_AI_AGENT_RE_RUN',
  G2_REVIEW = 'g2-review',
  EQUIFAX_DEMO = 'equifax-demo',
  FRAUD_FIGHTER_LOGO = 'fraud-fighter-logo',
  SAR_VIRTUALIZATION = 'sar-virtualization',
  MULTI_FILING_INSTITUTION_FINTRAC_STR = 'multi-filing-institution-fintrac-str',
  MULTI_FILING_INSTITUTION_SAR = 'multi-filing-institution-sar',
  ENABLE_CGDO_COMMENTS = 'enable-cgdo-comments',
  WORKFLOW_VISUALIZER = 'workflow-visualizer',
  SAVED_FILTERS = 'saved-filters',

  // RFI (Request for Information)
  RFI_ONBOARDING = 'RFI_ONBOARDING',
  LIVENESS_CHECK = 'LIVENESS_CHECK',

  // Queues
  QUEUE_DEADLINE_START_DATE = 'queue-deadline-start-date',

  // Risk Ratings
  CRR_MULTIPLE_LIVE_MODELS = 'crr-multiple-live-models',
  CRR_WATERFALL_SEGMENTATION = 'crr-waterfall-segmentation',
}

export interface FeatureFlags {
  [FeatureFlag.EDIT_CUSTOM_DATA]?: boolean;
  [FeatureFlag.SHOW_PAYER_PAYEE_TABS]?: boolean;
  [FeatureFlag.REMOVE_DISPLAY_TAGS]?: boolean;
  [FeatureFlag.SIXTRAN_SNOWFLAKE_EXPECTED_LAG]?: number;
  [FeatureFlag.BULK_ACTION_LIMIT]?: number;
  [FeatureFlag.ALERT_FROM_ALERT]?: boolean;
  [FeatureFlag.RULE_EVENT_SCHEMA_V2_1]?: boolean;
  [FeatureFlag.DMB_CUSTOM_TXN_DATETIME]?: boolean;
  [FeatureFlag.IMMUTABLE_NOTES]?: boolean;
  [FeatureFlag.SESSION_REPLAY]?: boolean;
  [FeatureFlag.WORKAROUND_HIDE_CURRENCY_CONFIG]?: boolean;
  [FeatureFlag.CLASSIFIED_QUEUES]?: boolean;
  [FeatureFlag.FRAUD_TRANSACTION_RULES]?: boolean;
  [FeatureFlag.WATCHLIST_ALERTS]?: boolean;
  [FeatureFlag.ACH_RISK_SCORE_FIELD]?: boolean;
  [FeatureFlag.HISTORIC_RISK_SCORES]?: boolean;
  [FeatureFlag.QUALITY_CYCLE_LIMIT]?: boolean;
  [FeatureFlag.WATCHLIST_PROVIDER]?: WatchlistProviders;
  [FeatureFlag.FEEDBACK_SURVEY_ID]?: number;
  [FeatureFlag.AI_ADMIN_DASHBOARD]?: boolean;
  [FeatureFlag.FINCEN_314A]?: boolean;
  [FeatureFlag.FINCEN_314A_MATCHING_LOGIC_VERSION]?: 'v1' | 'v2' | 'v3';
  [FeatureFlag.AI_AGENT]?: boolean;
  [FeatureFlag.AI_AGENT_DATA_EXPLORATION]?: boolean;
  [FeatureFlag.ASK_YOUR_ALERT]?: boolean;
  [FeatureFlag.BUILD_YOUR_OWN_AGENT]?: boolean;
  [FeatureFlag.ENABLE_FILING_AI_AGENTS]?: boolean;
  [FeatureFlag.AI_DECISIONING_V0]?: boolean;
  [FeatureFlag.SEARCH_V2]?: boolean;
  [FeatureFlag.ENTITY_PHONE_NUMBER_SEARCH_V2]?: boolean;
  [FeatureFlag.ALLOW_AI_AGENT_RE_RUN]?: boolean;
  [FeatureFlag.G2_REVIEW]?: boolean;
  [FeatureFlag.FINTRAC_STR]?: boolean;
  [FeatureFlag.FINTRAC_STR_UPLOAD]?: boolean;
  [FeatureFlag.FINTRAC_FORM_GENERATOR_REFACTOR]?: boolean;
  [FeatureFlag.FINCEN_DOEP_FILING]?: boolean;
  [FeatureFlag.FRAUD_FIGHTER_LOGO]?: boolean;
  [FeatureFlag.SAR_VIRTUALIZATION]?: boolean;
  [FeatureFlag.ONLINE_RESEARCH_BYOT]?: boolean;
  [FeatureFlag.G_GROUNDING_ONLINE_RESEARCH]?: boolean;
  [FeatureFlag.RULE_RECOMMENDATIONS]?: boolean;
  [FeatureFlag.ALERT_COMPONENT_BUTTONS]?: boolean;
  [FeatureFlag.DISPOSITION_ACTIONS]?: boolean;
  [FeatureFlag.ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING]?: boolean;
  [FeatureFlag.BULK_RESOLVE_FROM_QUEUED_VIEWS]?: boolean;
  [FeatureFlag.MULTI_CASE_FILING]?: boolean;
  [FeatureFlag.ADD_CASES_TO_SAR]?: boolean;
  [FeatureFlag.FINTRAC_STR_AUTOFILL_WORKFLOW]?: boolean;
  [FeatureFlag.FINTRAC_STR_AUTOFILL_DISPOSITION_COMMENT]?: boolean;
  [FeatureFlag.RULE_DELAY]?: boolean;
  [FeatureFlag.ACH_RISK_SCORE_SCENARIO_ENABLED]?: boolean;
  [FeatureFlag.FOCSEC_FIELDS_ENABLED]?: boolean;
  [FeatureFlag.RULE_LIFECYCLE_BANNER]?: boolean;
  [FeatureFlag.EQUIFAX_DEMO]?: boolean;
  [FeatureFlag.RULE_RECS_CREATE_RULE]?: boolean;
  [FeatureFlag.ROUND_ROBIN_ALERT_ASSIGNMENT]?: boolean;
  [FeatureFlag.ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT]?: boolean;
  [FeatureFlag.ENABLE_CGDO_COMMENTS]?: boolean;
  [FeatureFlag.WORKFLOW_VISUALIZER]?: boolean;
  [FeatureFlag.WATCHLIST_COMBINED_JOURNEYS]?: boolean;
  [FeatureFlag.SHOW_TRANSACTION_ACTIVITY]?: boolean;
  [FeatureFlag.TEAM_BASED_WORKFLOW_PERMISSIONS]?: boolean;
  [FeatureFlag.WORKFLOW_MANAGEMENT_REDESIGN]?: boolean;
  [FeatureFlag.FINTRAC_ERROR_CARDS]?: boolean;
  [FeatureFlag.RFI_ONBOARDING]?: boolean;
  [FeatureFlag.LIVENESS_CHECK]?: boolean;
  [FeatureFlag.AI_TEXT_TO_RULE]?: boolean;
  [FeatureFlag.GREENDOT_PIVOT_TABLES]?: boolean;
  [FeatureFlag.DMB_AI_FUZZY_SCORE]?: boolean;
  [FeatureFlag.MULTI_FILING_INSTITUTION_FINTRAC_STR]?: boolean;
  [FeatureFlag.MULTI_FILING_INSTITUTION_SAR]?: boolean;
  [FeatureFlag.SAVED_FILTERS]?: boolean;
  [FeatureFlag.REPORTING_DASHBOARD_V2]?: boolean;
  [FeatureFlag.RULES_AVOID_ALERT_GENERATION]?: boolean;

  // These are the Delta Lake Transition Flags
  [FeatureFlag.CM_DELTA_LAKE_ALERT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_ANALYSIS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_ACTION_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_HISTOGRAM]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_CGDO_SEARCH]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_TXN_ANALYSIS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_ENTITIES]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_ACTION_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_GOAML_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_FINTRAC_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FRAUD_DASHBOARD]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_RELATIONS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RISK_SCORE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_NETWORK_ANALYSIS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RELATIONS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_TXN_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_ACTION_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_GEOLOCATION]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_HASHED_ID_ROUTES]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID]?: boolean;
  [FeatureFlag.FFIP_ENRICHMENT_MATCHING]?: boolean;
  [FeatureFlag.PG_CACHE_AD_HOC_DATA_LOADER]?: boolean;

  // DM Feature Flags
  [FeatureFlag.VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS]?: boolean;
  [FeatureFlag.VELOCITY_RTR_MIXED_EVENT_TYPES]?: boolean;
  [FeatureFlag.VELOCITY_RTR_DEVICE_INTELLIGENCE_FIELDS]?: boolean;
  [FeatureFlag.RTR_ENTITY_ALLOWLISTING]?: boolean;
  [FeatureFlag.RTR_TRM_ENABLED]?: boolean;
  [FeatureFlag.DMB_FORM_REFACTOR]?: boolean;
  [FeatureFlag.GLOBAL_VARIABLES]?: boolean;
  [FeatureFlag.THRESHOLD_EXPLORER]?: boolean;
  [FeatureFlag.THRESHOLD_EXPLORER_DASHBOARDS]?: boolean;
  [FeatureFlag.SR_TRM_DMB_ENABLED]?: boolean;
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED]?: boolean;
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS]?: boolean;

  // DIM
  [FeatureFlag.CONSORTIUM_DMB]?: boolean;
  [FeatureFlag.DARK_WEB_ALERTS]?: boolean;
  [FeatureFlag.CONSORTIUM_ALERTS_SUMMARY]?: boolean;
  [FeatureFlag.CONSORTIUM_ALERTS_V2]?: boolean;

  // Queues
  [FeatureFlag.QUEUE_DEADLINE_START_DATE]?: boolean;

  [FeatureFlag.INSTRUMENT_TXN_ANALYSIS]?: boolean;

  [FeatureFlag.ACTION_ANALYSIS_UPDATE]?: boolean;

  // Risk Ratings
  [FeatureFlag.CRR_MULTIPLE_LIVE_MODELS]?: boolean;
  [FeatureFlag.CRR_WATERFALL_SEGMENTATION]?: boolean;

  // We use this flag to show the loading state
  [UNLOADED_FLAGS_KEY]?: boolean;
}

/**
 * A set of feature flags to fetch and their default values.
 *
 * If the flag is not exposed to the client, the default value will be used.
 * If communication with LaunchDarkly fails, the default value will be used.
 *
 * Note: This object should contain all flags defined in the FeatureFlag enum.
 * If a flag is missing, it will not be fetched and will always be undefined.
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  [FeatureFlag.EDIT_CUSTOM_DATA]: false,
  [FeatureFlag.SHOW_PAYER_PAYEE_TABS]: false,
  [FeatureFlag.REMOVE_DISPLAY_TAGS]: true,
  [FeatureFlag.SIXTRAN_SNOWFLAKE_EXPECTED_LAG]: 240,
  [FeatureFlag.BULK_ACTION_LIMIT]: 100,
  [FeatureFlag.ALERT_FROM_ALERT]: false,
  [FeatureFlag.RULE_EVENT_SCHEMA_V2_1]: true,
  [FeatureFlag.DMB_CUSTOM_TXN_DATETIME]: false,
  [FeatureFlag.IMMUTABLE_NOTES]: false,
  [FeatureFlag.SESSION_REPLAY]: false,
  [FeatureFlag.WORKAROUND_HIDE_CURRENCY_CONFIG]: false,
  [FeatureFlag.CLASSIFIED_QUEUES]: true,
  [FeatureFlag.FRAUD_TRANSACTION_RULES]: false,
  [FeatureFlag.WATCHLIST_ALERTS]: false,
  [FeatureFlag.ACH_RISK_SCORE_FIELD]: false,
  [FeatureFlag.HISTORIC_RISK_SCORES]: false,
  [FeatureFlag.QUALITY_CYCLE_LIMIT]: true,
  [FeatureFlag.WATCHLIST_PROVIDER]: WatchlistProviders.LEXISNEXIS,
  [FeatureFlag.FEEDBACK_SURVEY_ID]: 41877568,
  [FeatureFlag.AI_ADMIN_DASHBOARD]: false,
  [FeatureFlag.FINCEN_314A]: false,
  [FeatureFlag.FINCEN_314A_MATCHING_LOGIC_VERSION]: 'v2',
  [FeatureFlag.AI_AGENT]: false,
  [FeatureFlag.AI_AGENT_DATA_EXPLORATION]: false,
  [FeatureFlag.ASK_YOUR_ALERT]: false,
  [FeatureFlag.BUILD_YOUR_OWN_AGENT]: false,
  [FeatureFlag.ENABLE_FILING_AI_AGENTS]: false,
  [FeatureFlag.SEARCH_V2]: true,
  [FeatureFlag.ENTITY_PHONE_NUMBER_SEARCH_V2]: false,
  [FeatureFlag.ALLOW_AI_AGENT_RE_RUN]: false,
  [FeatureFlag.G2_REVIEW]: false,
  [FeatureFlag.FINTRAC_STR]: false,
  [FeatureFlag.FINTRAC_STR_UPLOAD]: false,
  [FeatureFlag.FINTRAC_FORM_GENERATOR_REFACTOR]: false,
  [FeatureFlag.FINCEN_DOEP_FILING]: false,
  [FeatureFlag.FRAUD_FIGHTER_LOGO]: false,
  [FeatureFlag.SAR_VIRTUALIZATION]: false,
  [FeatureFlag.ONLINE_RESEARCH_BYOT]: false,
  [FeatureFlag.G_GROUNDING_ONLINE_RESEARCH]: false,
  [FeatureFlag.RULE_RECOMMENDATIONS]: false,
  [FeatureFlag.ALERT_COMPONENT_BUTTONS]: false,
  [FeatureFlag.DISPOSITION_ACTIONS]: false,
  [FeatureFlag.ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING]: false,
  [FeatureFlag.BULK_RESOLVE_FROM_QUEUED_VIEWS]: false,
  [FeatureFlag.MULTI_CASE_FILING]: false,
  [FeatureFlag.ADD_CASES_TO_SAR]: false,
  [FeatureFlag.FINTRAC_STR_AUTOFILL_WORKFLOW]: false,
  [FeatureFlag.FINTRAC_STR_AUTOFILL_DISPOSITION_COMMENT]: false,
  [FeatureFlag.AI_DECISIONING_V0]: false,
  [FeatureFlag.RULE_DELAY]: false,
  [FeatureFlag.ACH_RISK_SCORE_SCENARIO_ENABLED]: false,
  [FeatureFlag.FOCSEC_FIELDS_ENABLED]: true,
  [FeatureFlag.RULE_LIFECYCLE_BANNER]: false,
  [FeatureFlag.EQUIFAX_DEMO]: false,
  [FeatureFlag.RULE_RECS_CREATE_RULE]: false,
  [FeatureFlag.ROUND_ROBIN_ALERT_ASSIGNMENT]: false,
  [FeatureFlag.ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT]: false,
  [FeatureFlag.ENABLE_CGDO_COMMENTS]: true,
  [FeatureFlag.WORKFLOW_VISUALIZER]: false,
  [FeatureFlag.WATCHLIST_COMBINED_JOURNEYS]: false,
  [FeatureFlag.SHOW_TRANSACTION_ACTIVITY]: true,
  [FeatureFlag.TEAM_BASED_WORKFLOW_PERMISSIONS]: false,
  [FeatureFlag.WORKFLOW_MANAGEMENT_REDESIGN]: false,
  [FeatureFlag.FINTRAC_ERROR_CARDS]: false,
  [FeatureFlag.RFI_ONBOARDING]: false,
  [FeatureFlag.LIVENESS_CHECK]: false,
  [FeatureFlag.AI_TEXT_TO_RULE]: false,
  [FeatureFlag.GREENDOT_PIVOT_TABLES]: false,
  [FeatureFlag.DMB_AI_FUZZY_SCORE]: false,
  [FeatureFlag.MULTI_FILING_INSTITUTION_FINTRAC_STR]: false,
  [FeatureFlag.MULTI_FILING_INSTITUTION_SAR]: false,
  [FeatureFlag.SAVED_FILTERS]: false,
  [FeatureFlag.REPORTING_DASHBOARD_V2]: false,

  // These are the Delta Lake Transition Flags
  [FeatureFlag.CM_DELTA_LAKE_ALERT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_ANALYSIS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_ACTION_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_HISTOGRAM]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_CGDO_SEARCH]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_TXN_ANALYSIS]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_ENTITIES]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_ACTION_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_GOAML_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_FINTRAC_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FRAUD_DASHBOARD]: false,
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_RELATIONS]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RISK_SCORE]: false,
  [FeatureFlag.CM_DELTA_LAKE_NETWORK_ANALYSIS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RELATIONS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_TXN_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_ACTION_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_GEOLOCATION]: false,
  [FeatureFlag.CM_DELTA_LAKE_HASHED_ID_ROUTES]: false,
  [FeatureFlag.CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID]: false,
  [FeatureFlag.FFIP_ENRICHMENT_MATCHING]: false,
  [FeatureFlag.PG_CACHE_AD_HOC_DATA_LOADER]: false,

  // DM Feature Flags
  [FeatureFlag.VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS]: false,
  [FeatureFlag.VELOCITY_RTR_MIXED_EVENT_TYPES]: false,
  [FeatureFlag.VELOCITY_RTR_DEVICE_INTELLIGENCE_FIELDS]: false,
  [FeatureFlag.RTR_ENTITY_ALLOWLISTING]: false,
  [FeatureFlag.RTR_TRM_ENABLED]: false,
  [FeatureFlag.DMB_FORM_REFACTOR]: false,
  [FeatureFlag.GLOBAL_VARIABLES]: false,
  [FeatureFlag.THRESHOLD_EXPLORER]: false,
  [FeatureFlag.THRESHOLD_EXPLORER_DASHBOARDS]: false,
  [FeatureFlag.SR_TRM_DMB_ENABLED]: false,
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED]: false,
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS]: false,

  // DIM
  [FeatureFlag.DARK_WEB_ALERTS]: false,
  [FeatureFlag.CONSORTIUM_DMB]: false,
  [FeatureFlag.CONSORTIUM_ALERTS_SUMMARY]: false,
  [FeatureFlag.CONSORTIUM_ALERTS_V2]: false,

  // Instrument Transaction Analysis
  [FeatureFlag.INSTRUMENT_TXN_ANALYSIS]: false,

  // Activity Analysis Update
  [FeatureFlag.ACTION_ANALYSIS_UPDATE]: false,

  // Queues
  [FeatureFlag.QUEUE_DEADLINE_START_DATE]: false,

  // Risk Ratings
  [FeatureFlag.CRR_MULTIPLE_LIVE_MODELS]: false,
  [FeatureFlag.CRR_WATERFALL_SEGMENTATION]: false,

  // Rules
  [FeatureFlag.RULES_AVOID_ALERT_GENERATION]: false,
} as const satisfies Record<FeatureFlag, string | number | boolean>;

export interface FeatureFlagsState {
  flags: FeatureFlags;
}
